// Create a custom role (F-IAM-11, spec/07 §6.2): a name and the permissions it combines. The name is
// lower case letters, digits and underscores (the contract's `RoleName`), and at least one permission is
// picked from the catalogue before it can be saved. The server refuses a name a built-in or another
// custom role already uses (409) and rejects a permission it does not assign; both come back on the form.

import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Notice, Section } from '../../ui';
import { PermissionPicker } from './PermissionPicker';
import { useCreateRole } from './queries';

/** The contract's `RoleName` shape (`members.ts`): a letter, then letters, digits and underscores. */
const ROLE_NAME = /^[a-z][a-z0-9_]{0,63}$/;

export function CreateRoleDialog({
  organizationId,
  catalogue,
  open,
  onOpenChange,
}: {
  readonly organizationId: string;
  readonly catalogue: readonly string[];
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" dirty="auto" title={t('staff.role.create.title')} description={t('staff.role.create.intro')}>
      {/* Remounted per open, so each new role starts from an empty name and no permissions. */}
      {open ? <CreateRoleForm organizationId={organizationId} catalogue={catalogue} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function CreateRoleForm({
  organizationId,
  catalogue,
  onDone,
}: {
  readonly organizationId: string;
  readonly catalogue: readonly string[];
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const create = useCreateRole(organizationId);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [errors, setErrors] = useState<{ name?: string; permissions?: string; form?: string }>({});

  function toggle(permission: string, on: boolean): void {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(permission);
      else next.delete(permission);
      return next;
    });
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: { name?: string; permissions?: string } = {};
    if (!ROLE_NAME.test(name.trim())) next.name = t('staff.role.create.nameInvalid');
    if (selected.size === 0) next.permissions = t('staff.role.create.selectAtLeastOne');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    create.mutate(
      { name: name.trim(), permissions: [...selected] },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('staff.role.create.created', { name: name.trim() }) });
          onDone();
        },
        onError: (error) => {
          const nameMessage = problem.fieldMessage(error, 'name');
          if (problemCode(error) === PROBLEM.conflict) setErrors({ name: t('staff.role.create.nameTaken') });
          else if (nameMessage !== undefined) setErrors({ name: nameMessage });
          else setErrors({ form: problem.text(error, { fallback: t('staff.role.create.refused') }) });
        },
      },
    );
  }

  return (
    <form className="console-form" aria-label={t('staff.role.create.title')} noValidate onSubmit={submit}>
      {errors.form !== undefined ? (
        <Notice tone="danger" role="alert">
          {errors.form}
        </Notice>
      ) : null}
      <TextField
        label={t('staff.role.create.nameLabel')}
        description={t('staff.role.create.nameHint')}
        required
        value={name}
        onChange={(event) => {
          setName(event.target.value);
          if (errors.name !== undefined) setErrors((current) => ({ ...current, name: undefined }));
        }}
        onBlur={() => setErrors((current) => ({ ...current, name: name.trim() === '' || ROLE_NAME.test(name.trim()) ? undefined : t('staff.role.create.nameInvalid') }))}
        error={errors.name}
      />
      <Section title={t('staff.role.create.permissions')} description={t('staff.role.create.permissionsHint')}>
        {errors.permissions !== undefined ? (
          <Notice tone="danger" role="alert">
            {errors.permissions}
          </Notice>
        ) : null}
        <PermissionPicker catalogue={catalogue} selected={selected} onToggle={toggle} />
      </Section>
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending}>
          {t('staff.role.create.submit')}
        </Button>
      </div>
    </form>
  );
}
